import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ProtectedRoute from "./components/common/ProtectedRoute";
import { Skeleton } from "@/components/ui/skeleton";
import MainLayout from "./components/layout/MainLayout";
import { Toaster } from "@/components/ui/toaster";
import ScrollToTop from "./components/common/ScrollToTop";
import { initNativePlatform } from "./lib/nativeUtils";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetails = lazy(() => import("./pages/JobDetails"));
const Applications = lazy(() => import("./pages/Applications"));
const Companies = lazy(() => import("./pages/Companies"));
const CompanyDetails = lazy(() => import("./pages/CompanyDetails"));
const PostJob = lazy(() => import("./pages/PostJob"));
const Employers = lazy(() => import("./pages/Employers"));
const EmployerProfile = lazy(() => import("./pages/EmployerProfile"));
const Search = lazy(() => import("./pages/Search"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Profile = lazy(() => import("./pages/ProfileEnhanced"));
const CommunityComponents = lazy(() => import("./pages/CommunityComponents"));
const CommunityServices = lazy(() => import("./pages/CommunityServices"));
const BlogComponents = lazy(() => import("./pages/BlogComponents"));
const BlogServices = lazy(() => import("./pages/BlogServices"));
const BlogHome = lazy(() => import("./pages/BlogHome"));
const BlogPostView = lazy(() => import("./pages/BlogPostView"));
const BlogCategories = lazy(() => import("./pages/BlogCategories"));
const BlogTags = lazy(() => import("./pages/BlogTags"));
const CommunityHome = lazy(() => import("./pages/CommunityHome"));
const CommunityQuestions = lazy(() => import("./pages/CommunityQuestions"));
const BlogSubmission = lazy(() => import("./pages/BlogSubmission"));
const AuthorProfile = lazy(() => import("./pages/AuthorProfile"));
const EmployerDashboard = lazy(() => import("./pages/EmployerDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const EmployerProfileManagement = lazy(() => import("./pages/EmployerProfileManagement"));
const EmployerProfileSetup = lazy(() => import("./pages/EmployerProfileSetup"));
const EmployerJobManagement = lazy(() => import("./pages/EmployerJobManagement"));
const EmployerApplicationManagement = lazy(() => import("./pages/EmployerApplicationManagement"));
const ApplicationDetail = lazy(() => import("./pages/ApplicationDetail"));
const EmployerCandidateSearch = lazy(() => import("./pages/EmployerCandidateSearch"));
const EmployerSettings = lazy(() => import("./pages/EmployerSettings"));
const NotificationsPage = lazy(() => import("./pages/Notifications"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const Settings = lazy(() => import("./pages/Settings"));
const HiringSolutions = lazy(() => import("./pages/HiringSolutions"));
const EmployerBranding = lazy(() => import("./pages/EmployerBranding"));
const CampusRecruitment = lazy(() => import("./pages/CampusRecruitment"));
const TargetedAds = lazy(() => import("./pages/TargetedAds"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const SalaryCalculator = lazy(() => import("./pages/SalaryCalculator"));
const JobAlerts = lazy(() => import("./pages/JobAlerts"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const Careers = lazy(() => import("./pages/Careers"));
const Press = lazy(() => import("./pages/Press"));
const AiJobAgent = lazy(() => import("./pages/AiJobAgent"));

// Legal Pages
const TermsOfUse = lazy(() => import("./pages/legal/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const AccessibilityPage = lazy(() => import("./pages/legal/Accessibility"));
const Disclaimer = lazy(() => import("./pages/legal/Disclaimer"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background p-8">
    <div className="container mx-auto space-y-6">
      <Skeleton className="h-12 w-64" />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function App() {
  // Initialize native Android features (back-button, status bar)
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    initNativePlatform().then((fn) => { cleanup = fn; });
    return () => { if (cleanup) cleanup(); };
  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <ErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <TooltipProvider>
                <Toaster />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/login" element={<Auth />} />
                    <Route element={<MainLayout />}>
                      <Route path="/" element={<Index />} />
                      <Route path="/jobs" element={<Jobs />} />
                      <Route path="/jobs/:id" element={<JobDetails />} />
                      <Route
                        path="/applications"
                        element={
                          <ProtectedRoute>
                            <Applications />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/companies" element={<Companies />} />
                      <Route path="/companies/:id" element={<CompanyDetails />} />
                      <Route
                        path="/post-job"
                        element={
                          <ProtectedRoute requiredRoles={["EMPLOYER", "ADMIN"]}>
                            <PostJob />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/edit-job/:id"
                        element={
                          <ProtectedRoute requiredRoles={["EMPLOYER", "ADMIN"]}>
                            <PostJob />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/employers" element={<Employers />} />
                      <Route
                        path="/employers/me"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerProfile />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/employers/:id" element={<EmployerProfile />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <Settings />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/career-advice" element={<Navigate to="/blog" replace />} />
                      <Route path="/coming-soon" element={<ComingSoon />} />
                      <Route path="/solutions" element={<HiringSolutions />} />
                      <Route path="/branding" element={<EmployerBranding />} />
                      <Route path="/campus" element={<CampusRecruitment />} />
                      <Route path="/ads" element={<TargetedAds />} />
                      <Route path="/community/components" element={<CommunityComponents />} />
                      <Route path="/community/services" element={<CommunityServices />} />
                      <Route path="/community" element={<CommunityHome />} />
                      <Route path="/community/questions" element={<CommunityQuestions />} />
                      <Route path="/community/questions/:questionId" element={<CommunityQuestions />} />
                      <Route path="/blog/components" element={<BlogComponents />} />
                      <Route path="/blog/services" element={<BlogServices />} />
                      <Route path="/blog" element={<BlogHome />} />
                      <Route path="/blog/categories" element={<BlogCategories />} />
                      <Route path="/blog/categories/:categoryId" element={<BlogCategories />} />
                      <Route path="/blog/tags" element={<BlogTags />} />
                      <Route path="/blog/submit" element={<BlogSubmission />} />
                      <Route path="/blog/authors" element={<AuthorProfile />} />
                      <Route path="/blog/authors/:authorId" element={<AuthorProfile />} />
                      <Route path="/blog/:slug" element={<BlogPostView />} />


                      {/* Legal Routes */}
                      <Route path="/terms" element={<TermsOfUse />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/cookies" element={<CookiePolicy />} />
                      <Route path="/disclaimer" element={<Disclaimer />} />
                      <Route path="/accessibility" element={<AccessibilityPage />} />
                      <Route path="/contact" element={<ContactUs />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/salary-calculator" element={<SalaryCalculator />} />
                      <Route path="/job-alerts" element={<JobAlerts />} />
                      <Route path="/sitemap" element={<SitemapPage />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/press" element={<Press />} />
                      <Route
                        path="/ai-agent"
                        element={
                          <ProtectedRoute>
                            <AiJobAgent />
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin Routes */}
                      <Route
                        path="/admin-dashboard"
                        element={
                          <ProtectedRoute requiredRole="ADMIN">
                            <AdminDashboard />
                          </ProtectedRoute>
                        }
                      />

                      {/* Employer Routes */}
                      <Route
                        path="/employer-dashboard"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employer/profile/setup"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerProfileSetup />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employer-profile"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerProfileManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employer-jobs"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerJobManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employer-applications"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerApplicationManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/application/:id"
                        element={
                          <ProtectedRoute>
                            <ApplicationDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employer-candidates"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerCandidateSearch />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employer-settings"
                        element={
                          <ProtectedRoute requiredRole="EMPLOYER">
                            <EmployerSettings />
                          </ProtectedRoute>
                        }
                      />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </TooltipProvider>
            </AuthProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}


